import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

import { type AssetPickerConstraints } from "./assetPickerConstraints";
import { AssetSearchInput } from "./AssetSearchInput";

import { type AssetMessage } from "@/api/connectAssets";
import { listMediaLibraryAssets, skuStoreKeys } from "@/api/products";
import { catalogAssetToAssetMessage } from "@/features/AssetLibrary/utils/catalogAssetAdapter";
import { uploadManager } from "@/features/AssetUpload/uploadManager";
import { defaultAssetMetadata } from "@/features/AssetUpload/useUploads";
import {
  buildRejectionMessage,
  filterValidFiles,
} from "@/features/AssetUpload/validation";
import { formatFileSize } from "@/features/ListsV2/utils/csv";
import {
  CheckCircleIcon,
  CloudUploadOutlinedIcon,
  ImageOutlinedIcon,
  InsertDriveFileOutlinedIcon,
} from "@/icons";

interface AssetPickerModalProps {
  open: boolean;
  siteId: string;
  constraints: AssetPickerConstraints;
  onSelect: (asset: AssetMessage) => void;
  onClose: () => void;
}

type TabValue = "library" | "upload";

const ASSETS_PER_PAGE = 24;

function validateAsset(
  asset: AssetMessage,
  constraints: AssetPickerConstraints,
): string | null {
  if (!constraints.isSelectable(asset.media_type)) {
    return constraints.unsupportedTypeMessage;
  }
  const { maxSizeBytes } = constraints;
  const sizeBytes = asset.size_bytes != null ? Number(asset.size_bytes) : 0;
  if (maxSizeBytes != null && sizeBytes > maxSizeBytes) {
    return `This file is too large (${formatFileSize(sizeBytes)}). Maximum size is ${formatFileSize(maxSizeBytes)}.`;
  }
  return null;
}

export function AssetPickerModal({
  open,
  siteId,
  constraints,
  onSelect,
  onClose,
}: AssetPickerModalProps) {
  const [tab, setTab] = useState<TabValue>("library");
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setTab("library");
      setSearch("");
      setSelectedIndex(null);
    }
  }, [open]);

  const searchTerm = search.trim();
  const { assetTypes } = constraints;

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    // Under the catalogAssets prefix so upload/delete invalidation refreshes it.
    queryKey: [
      ...skuStoreKeys.catalogAssets(siteId),
      "picker",
      { q: searchTerm, type: assetTypes },
    ],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      listMediaLibraryAssets(siteId, {
        q: searchTerm,
        type: assetTypes,
        approval_status: ["approved", "draft"],
        sort_by: "newest",
        limit: ASSETS_PER_PAGE,
        next: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    initialPageParam: undefined as number | undefined,
    enabled: open && !!siteId,
    staleTime: 15_000,
    // Keep the current grid up while the next search loads.
    placeholderData: keepPreviousData,
  });

  const assets = (data?.pages.flatMap((page) => page.assets) ?? []).map(
    catalogAssetToAssetMessage,
  );
  const selectableAssets = assets.filter((a) =>
    constraints.isSelectable(a.media_type),
  );

  const handleSelect = () => {
    const selectedAsset =
      selectedIndex != null ? selectableAssets[selectedIndex] : null;
    if (!selectedAsset) return;

    const validationError = validateAsset(selectedAsset, constraints);
    if (validationError) {
      enqueueSnackbar(validationError, { variant: "error" });
      return;
    }

    onSelect(selectedAsset);
    onClose();
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setSelectedIndex(null);
  }, []);

  const handleUploadComplete = useCallback(() => {
    // uploadManager invalidates the catalog assets query on batch-settle,
    // so the library refetches on its own; just return the user to it.
    setTab("library");
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { height: "80vh", maxHeight: 720 } } }}
    >
      <DialogTitle>{constraints.title}</DialogTitle>
      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", p: 0 }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
          <Tabs value={tab} onChange={(_, v: TabValue) => setTab(v)}>
            <Tab label="Asset Library" value="library" />
            <Tab label="Upload" value="upload" />
          </Tabs>
        </Box>

        {tab === "library" && (
          <Stack sx={{ flex: 1, overflow: "hidden", p: 3 }} spacing={2}>
            <AssetSearchInput
              value={search}
              onDebouncedChange={handleSearchChange}
              debounceMs={300}
            />
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {isLoading ? (
                <AssetPickerGridSkeleton />
              ) : selectableAssets.length === 0 && !hasNextPage ? (
                <AssetPickerEmptyState
                  emptyLabel={constraints.emptyLabel}
                  hasSearch={search.length > 0}
                  onClearSearch={() => {
                    setSearch("");
                    setSelectedIndex(null);
                  }}
                  onSwitchToUpload={() => setTab("upload")}
                />
              ) : (
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 2,
                      opacity: isFetching && !isFetchingNextPage ? 0.6 : 1,
                      transition: "opacity 0.15s",
                    }}
                  >
                    {selectableAssets.map((asset, index) => (
                      <SelectableAssetCard
                        key={`${asset.asset_key}-${index}`}
                        asset={asset}
                        selected={selectedIndex === index}
                        onClick={() =>
                          setSelectedIndex((prev) =>
                            prev === index ? null : index,
                          )
                        }
                      />
                    ))}
                  </Box>
                  {hasNextPage && (
                    <Stack alignItems="center" sx={{ pt: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() => void fetchNextPage()}
                        disabled={isFetchingNextPage}
                      >
                        {isFetchingNextPage ? "Loading…" : "Load more"}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        )}

        {tab === "upload" && (
          <UploadDropzone
            siteId={siteId}
            constraints={constraints}
            onUploadStarted={handleUploadComplete}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSelect}
          disabled={selectedIndex == null}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function mediaClassLabel(mediaType: string): string {
  if (mediaType.startsWith("image/")) return "IMAGE";
  if (mediaType.startsWith("video/")) return "VIDEO";
  if (mediaType.startsWith("audio/")) return "AUDIO";
  return "FILE";
}

function SelectableAssetCard({
  asset,
  selected,
  onClick,
}: {
  asset: AssetMessage;
  selected: boolean;
  onClick: () => void;
}) {
  const isImage = asset.media_type.startsWith("image/");
  const sizeLabel =
    asset.size_bytes != null ? formatFileSize(Number(asset.size_bytes)) : null;

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Select ${asset.original_filename}`}
      aria-pressed={selected}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        cursor: "pointer",
        userSelect: "none",
        border: "1px solid",
        borderColor: selected
          ? "primary.main"
          : "tokens.outlines.defaultBordersLight",
        boxShadow: selected
          ? (theme) => `0 0 0 1px ${theme.palette.primary.main}`
          : "none",
        borderRadius: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
        transition: "border-color 0.15s, box-shadow 0.15s",
        position: "relative",
        "&:hover": {
          borderColor: "primary.main",
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      {selected && (
        <CheckCircleIcon
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
            color: "primary.main",
            backgroundColor: "background.paper",
            borderRadius: "50%",
            fontSize: 24,
          }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          px: 2,
          py: "4px",
          color: "text.secondary",
          backgroundColor: "background.paper-elevation-1",
          letterSpacing: 0.5,
        }}
      >
        {mediaClassLabel(asset.media_type)}
      </Typography>
      <Box
        sx={{
          aspectRatio: "1 / 1",
          backgroundColor: "background.paper-elevation-1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {isImage && asset.cdn_url ? (
          <Box
            component="img"
            src={asset.cdn_url}
            alt={asset.original_filename}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <InsertDriveFileOutlinedIcon
            sx={{ fontSize: 48, color: "text.disabled" }}
          />
        )}
      </Box>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: "4px" }}>
        <Typography
          variant="subtitle2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {asset.original_filename}
        </Typography>
        {sizeLabel && (
          <Typography variant="caption" color="text.secondary">
            {sizeLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function AssetPickerGridSkeleton() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
        gap: 2,
      }}
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "tokens.outlines.defaultBordersLight",
          }}
        >
          <Skeleton variant="text" width="30%" sx={{ mx: 2, my: "4px" }} />
          <Skeleton variant="rectangular" sx={{ aspectRatio: "1 / 1" }} />
          <Box sx={{ p: 2 }}>
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="40%" />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function AssetPickerEmptyState({
  emptyLabel,
  hasSearch,
  onClearSearch,
  onSwitchToUpload,
}: {
  emptyLabel: string;
  hasSearch: boolean;
  onClearSearch: () => void;
  onSwitchToUpload: () => void;
}) {
  if (hasSearch) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
        <ImageOutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
        <Typography color="text.secondary">
          No images match your search
        </Typography>
        <Button variant="outlined" onClick={onClearSearch}>
          Clear search
        </Button>
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
      <ImageOutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
      <Typography color="text.secondary">{emptyLabel}</Typography>
      <Button variant="contained" onClick={onSwitchToUpload}>
        Upload an image
      </Button>
    </Stack>
  );
}

function UploadDropzone({
  siteId,
  constraints,
  onUploadStarted,
}: {
  siteId: string;
  constraints: AssetPickerConstraints;
  onUploadStarted: () => void;
}) {
  const { normalizeFile } = constraints;

  const dropzone = useDropzone({
    accept: constraints.uploadAccept,
    onDrop: (accepted) => {
      const normalized = normalizeFile ? accepted.map(normalizeFile) : accepted;
      const { accepted: ok, rejected } = filterValidFiles(normalized);
      if (rejected.length > 0) {
        enqueueSnackbar(buildRejectionMessage(rejected), { variant: "error" });
      }
      if (ok.length > 0) {
        uploadManager.addFiles(siteId, ok, defaultAssetMetadata());
        enqueueSnackbar(
          `${ok.length} file${ok.length > 1 ? "s" : ""} uploading. Select from the library once complete.`,
          { variant: "info" },
        );
        onUploadStarted();
      }
    },
  });

  return (
    <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, p: 3 }}>
      <Box
        {...dropzone.getRootProps()}
        sx={{
          width: "100%",
          maxWidth: 480,
          border: "2px dashed",
          borderColor: dropzone.isDragActive ? "primary.main" : "divider",
          borderRadius: 2,
          p: 6,
          textAlign: "center",
          cursor: "pointer",
          transition: "border-color 0.15s",
        }}
      >
        <input {...dropzone.getInputProps()} />
        <Stack alignItems="center" spacing={2}>
          <CloudUploadOutlinedIcon
            sx={{ fontSize: 48, color: "text.secondary" }}
          />
          <Typography variant="body1">
            Drag & drop files here, or click to browse
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {constraints.uploadHint}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
}
